import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Body1, Body2, Button } from "@activecollab/components";
import classnames from "classnames";
import moment from "moment";
import PropTypes from "prop-types";
import { useConversationContentContext } from "~/components/Chat/context/ConversationContentContext";
import AngDropFiles from "~/components/component.ang_drop_files";
import ProjectsModal from "~/components/project/ProjectsModal";
import { showSuccessToast, showErrorToast } from "~/components/Toast";
import { addConversationMessage } from "~/store/actions/actions.conversationMessages";
import { deleteConversationMessage } from "~/store/actions/actions.conversationMessages";
import { fetchConversationMessages } from "~/store/actions/actions.conversationMessages";
import { editConversationMessage } from "~/store/actions/actions.conversationMessages";
import { addTaskFromConversation } from "~/store/actions/actions.tasks";
import { makeGetLoggedUserLastMessageByConversationSelector } from "~/store/selectors/selector.conversationMessages";
import { makeGetCursorPaginationSelector } from "~/store/selectors/selector.cursorPagination";
import { getLoggedUserId } from "~/store/selectors/selector.users";
import { Body } from "./Body";
import { Footer } from "./Footer";
import { CREATE_TASK_MODE } from "./index.jsx";

const ConversationContentInner = ({ id, type, members, onClose }) => {
  const dispatch = useDispatch();
  const ref = useRef(null);
  const bodyRef = useRef(null);
  const { t } = useTranslation();

  const { mode, setMode, selectedMessageIds, newMessagesSince, setNewMessagesSince } = useConversationContentContext();

  const [showProjectList, setShowProjectList] = useState(false);
  const loggedUserId = useSelector(getLoggedUserId);
  const [loading, setLoading] = useState(false);
  const [editMessageId, setEditMessageId] = useState(0);
  const getConversationCursor = useMemo(() => makeGetCursorPaginationSelector("conversations", id), [id]);
  const pagination = useSelector(getConversationCursor);
  const { cursor, lastId } = pagination ?? {};
  const getLoggedUserLastMessage = useMemo(() => {
    return makeGetLoggedUserLastMessageByConversationSelector(id);
  }, [id]);
  const loggedUserLastMessage = useSelector(getLoggedUserLastMessage);
  const loggedUserLastMessageId = loggedUserLastMessage?.id || 0;

  const blockWriting = useMemo(() => {
    return type === "OneToOneConversation" && members.length === 1 && members[0] === loggedUserId;
  }, [members, loggedUserId, type]);

  React.useEffect(() => {
    if (typeof cursor === "undefined") {
      dispatch(fetchConversationMessages(id));
    }
  }, [cursor, dispatch, id]);

  const handleSubmit = useCallback(
    (attributes) => {
      if (editMessageId === 0) {
        const timestamp = moment.utc().unix();
        setNewMessagesSince((prevState) => {
          return typeof prevState === "undefined" ? prevState : timestamp;
        });
        dispatch(
          addConversationMessage(id, {
            ...attributes,
            created_by_id: loggedUserId,
            created_on: timestamp,
            conversation_id: id,
          })
        ).then(() => {
          setNewMessagesSince((prevState) => {
            return typeof prevState === "undefined" ? prevState : moment.utc().unix();
          });
        });
      } else {
        dispatch(editConversationMessage(id, editMessageId, attributes));
        setEditMessageId(0);
      }
      // scroll to start of message list when
      // logged user add new message
      if (bodyRef.current && editMessageId === 0) {
        bodyRef.current.scrollTop = 0;
      }
    },
    [editMessageId, setNewMessagesSince, dispatch, id, loggedUserId]
  );

  const handleResend = useCallback(
    (attributes, messageId) => {
      const isEdit = Number.isInteger(messageId);

      if (isEdit) {
        dispatch(editConversationMessage(id, messageId, attributes));
      } else {
        dispatch(addConversationMessage(id, attributes, messageId));

        if (bodyRef.current) {
          bodyRef.current.scrollTop = 0;
        }
      }
    },
    [dispatch, id]
  );

  const handleFetchMore = useCallback(async () => {
    if (!loading && cursor) {
      const beforeTop = bodyRef.current?.scrollTop || 0;
      setLoading(true);
      await dispatch(fetchConversationMessages(id, cursor, lastId));
      setLoading(false);
      if (bodyRef.current) {
        bodyRef.current.scrollTop = beforeTop;
      }
    }
  }, [loading, cursor, dispatch, id, lastId]);

  const handleCloseOptions = useCallback(() => {
    ref.current?.focus();
  }, []);

  const handleDeleteMessage = useCallback(
    (messageId) => {
      handleCloseOptions();
      return dispatch(deleteConversationMessage(id, messageId));
    },
    [dispatch, id, handleCloseOptions]
  );

  const handleEdit = useCallback((id) => {
    setEditMessageId(id);
  }, []);

  const handleCancel = useCallback(() => {
    editMessageId > 0 ? setEditMessageId(0) : typeof onClose === "function" && onClose();
  }, [editMessageId, onClose]);

  const handleStartEditLastMessage = useCallback(() => {
    if (loggedUserLastMessageId > 0) {
      setEditMessageId(loggedUserLastMessageId);
    }
  }, [loggedUserLastMessageId]);
  const handleOpenProjectList = useCallback(() => {
    setShowProjectList(true);
  }, []);

  const handleCloseProjectList = useCallback(() => {
    setShowProjectList(false);
  }, []);

  const handleChooseProject = useCallback(
    (projectId) => {
      setShowProjectList(false);
      setMode("");
      dispatch(
        addTaskFromConversation(id, {
          message_ids: selectedMessageIds,
          project_id: projectId,
        })
      ).then((action) => {
        const { response = {} } = action;
        const { result = {} } = response;
        const { single: taskId } = result;

        if (taskId) {
          showSuccessToast(t("Task created"));
          // updateRouteParams({ modal: `Task-${taskId}-${projectId}` });
        } else {
          showErrorToast(t("Failed to create task"));
        }
      });
    },
    // [dispatch, id, selectedMessageIds, updateRouteParams, t]
    [dispatch, id, selectedMessageIds, setMode, t]
  );

  return (
    <div
      className={classnames(
        "tw-flex",
        "tw-flex-1",
        "tw-flex-col",
        "tw-grow",
        "tw-overflow-y-auto",
        "tw-h-full",
        "tw-w-full"
      )}
      ref={ref}
      tabIndex={0}
    >
      <Body
        ref={bodyRef}
        conversationId={id}
        loggedUserId={loggedUserId}
        onFetchMore={handleFetchMore}
        loading={loading}
        onDelete={handleDeleteMessage}
        onEdit={handleEdit}
        onClose={handleCloseOptions}
        onResend={handleResend}
        type={type}
        newMessagesSince={newMessagesSince}
      />
      {blockWriting && mode === "" ? (
        <div
          className="hub-footer tw-text-center"
          style={{ padding: "18px" }}
        >
          <Body2
            color="tertiary"
            align="center"
          >
            {t("You cannot reply to this conversation.")}
          </Body2>
        </div>
      ) : null}
      {mode === "" && !blockWriting ? (
        <Footer
          conversationId={id}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          editMessageId={editMessageId}
          onStartEditLastMessage={handleStartEditLastMessage}
        />
      ) : null}
      {mode === CREATE_TASK_MODE ? (
        <Button
          variant="primary"
          className="tw-w-full tw-rounded-none tw-flex tw-shrink-0 tw-mt-auto tw-max-w-none"
          onClick={handleOpenProjectList}
          style={{ height: "57px" }}
          disabled={!selectedMessageIds.length}
        >
          <Body1
            weight="bold"
            align="center"
            color="page-paper"
          >
            {t("Create Task")}
          </Body1>
        </Button>
      ) : null}
      {mode === "" && !blockWriting ? <AngDropFiles context={`conversation-${id}`} /> : null}
      <ProjectsModal
        open={showProjectList}
        onClose={handleCloseProjectList}
        onChooseProject={handleChooseProject}
      />
    </div>
  );
};

ConversationContentInner.propTypes = {
  id: PropTypes.number.isRequired,
  type: PropTypes.string,
  members: PropTypes.arrayOf(PropTypes.number),
  onClose: PropTypes.func,
};

ConversationContentInner.defaultProps = {
  members: [],
};

ConversationContentInner.displayName = "ConversationContentInner";

export default ConversationContentInner;
